/**
 * Copy-paste snippets for the Integrations page.
 *
 * Pure string builders, kept out of `Integrations.tsx` so the page component
 * owns rendering and token state only. Every builder takes the caller's token
 * so the rendered snippet works as-is once the visitor mints one; signed-out
 * visitors get a `<YOUR_TOKEN>` placeholder from the caller.
 */

/** Public home of the SDK sources — the monorepo behind both npm packages. */
export const REPO_URL = 'https://github.com/OculusMindAI/oculusmind-agents-sdk';

/** The base URL the published MCP server assumes when none is configured. */
const SDK_DEFAULT_BASE_URL = 'https://api.oculusmind.ai';

interface PackageLinks {
  /** npm package name, as installed. */
  readonly name: string;
  /** Package page on npmjs.com. */
  readonly npmUrl: string;
  /** Package sources inside the monorepo. */
  readonly sourceUrl: string;
}

/** The two published npm packages, with their npm and GitHub destinations. */
export const PACKAGES: { readonly sdk: PackageLinks; readonly mcp: PackageLinks } = {
  sdk: {
    name: '@oculusmind-ai/agents',
    npmUrl: 'https://www.npmjs.com/package/@oculusmind-ai/agents',
    sourceUrl: `${REPO_URL}/tree/main/clients/typescript`,
  },
  mcp: {
    name: '@oculusmind-ai/mcp-server',
    npmUrl: 'https://www.npmjs.com/package/@oculusmind-ai/mcp-server',
    sourceUrl: `${REPO_URL}/tree/main/servers/mcp`,
  },
};

export type McpTab = 'claude' | 'json';

export type ProtocolTab = 'http' | 'curl' | 'ts';

const SAMPLE_BODY = {
  jsonrpc: '2.0',
  id: '1',
  method: 'message/send',
  params: {
    message: {
      kind: 'message',
      role: 'user',
      messageId: 'YOUR-MESSAGE-UUID',
      parts: [{ kind: 'text', text: 'What time is it?' }],
    },
  },
};

/**
 * `OCULUSMIND_BASE_URL` only needs to appear when this build talks to a host
 * other than the packages' built-in default (i.e. beta) — otherwise it is
 * noise in a snippet people paste into a terminal.
 */
function needsBaseUrl(apiBase: string): boolean {
  return apiBase !== SDK_DEFAULT_BASE_URL;
}

/** `claude mcp add` + the portable `mcpServers` JSON, for MCP hosts. */
export function buildMcpSnippets(token: string, apiBase: string): Record<McpTab, string> {
  const baseEnvFlag = needsBaseUrl(apiBase) ? `  --env OCULUSMIND_BASE_URL=${apiBase} \\\n` : '';
  const jsonEnv: Record<string, string> = { OCULUSMIND_TOKEN: token };
  if (needsBaseUrl(apiBase)) {
    jsonEnv.OCULUSMIND_BASE_URL = apiBase;
  }
  return {
    claude:
      `claude mcp add oculusmind \\\n` +
      `  --env OCULUSMIND_TOKEN=${token} \\\n` +
      baseEnvFlag +
      `  -- npx -y ${PACKAGES.mcp.name}`,
    json: JSON.stringify(
      {
        mcpServers: {
          oculusmind: { command: 'npx', args: ['-y', PACKAGES.mcp.name], env: jsonEnv },
        },
      },
      null,
      2,
    ),
  };
}

/** Raw JSON-RPC over HTTPS — the escape hatch for languages with no client. */
export function buildProtocolExamples(url: string, token: string): Record<ProtocolTab, string> {
  const u = new URL(url);
  const pretty = JSON.stringify(SAMPLE_BODY, null, 2);
  const compact = JSON.stringify(SAMPLE_BODY);
  return {
    http:
      `POST ${u.pathname} HTTP/1.1\n` +
      `Host: ${u.host}\n` +
      `Authorization: Bearer ${token}\n` +
      `Content-Type: application/json\n\n` +
      pretty,
    curl:
      `curl -X POST ${url} \\\n` +
      `  -H "Authorization: Bearer ${token}" \\\n` +
      `  -H "Content-Type: application/json" \\\n` +
      `  -d '${compact}'`,
    ts:
      `const res = await fetch(${JSON.stringify(url)}, {\n` +
      `  method: "POST",\n` +
      `  headers: {\n` +
      `    Authorization: \`Bearer \${token}\`,\n` +
      `    "Content-Type": "application/json",\n` +
      `  },\n` +
      `  body: JSON.stringify({\n` +
      `    jsonrpc: "2.0", id: "1", method: "message/send",\n` +
      `    params: { message: { kind: "message", role: "user",\n` +
      `      messageId: crypto.randomUUID(),\n` +
      `      parts: [{ kind: "text", text: "What time is it?" }] } },\n` +
      `  }),\n` +
      `});\n` +
      `const task = await res.json();`,
  };
}

/** Install line for the TypeScript SDK. */
export function buildSdkInstall(): string {
  return `npm install ${PACKAGES.sdk.name}`;
}

/** Smallest useful SDK program: construct a client, send one message. */
export function buildSdkUsage(token: string, apiBase: string, agentId: string): string {
  const baseUrlLine = needsBaseUrl(apiBase) ? `  baseUrl: ${JSON.stringify(apiBase)},\n` : '';
  return (
    `import { OculusMindAgents, StaticTokenProvider } from ${JSON.stringify(PACKAGES.sdk.name)};\n\n` +
    `const client = new OculusMindAgents({\n` +
    `  agent: ${JSON.stringify(agentId)},\n` +
    `  tokenProvider: new StaticTokenProvider(${JSON.stringify(token)}),\n` +
    baseUrlLine +
    `});\n\n` +
    `const reply = await client.send("What is the weather in Austin?");\n` +
    `console.log(reply.text);`
  );
}
